import {
  emptyAiForm,
  emptyManualForm,
  type AiCampaignForm,
  type AiPreviewData,
  type CreativePreview,
  type PreviewData,
  type SelectedScreenMeta,
  type StepThreeData,
} from '@/features/campaigns/types/campaign';
import type { BillboardCreateLaunchState } from '@/features/campaigns/types/billboardCreateLaunch';
import { loadAiCampaignDraft } from '@/features/campaigns/utils/aiCampaignDraftStorage';
import {
  clearStandardCampaignDraft,
  loadStandardCampaignDraft,
} from '@/features/campaigns/utils/standardCampaignDraftStorage';
import { resolveCreativePreview } from '@/features/campaigns/utils/resolveCreativePreview';

function isBrowserReload() {
  if (typeof performance === 'undefined') return false;
  try {
    const nav = performance.getEntriesByType(
      'navigation',
    )[0] as PerformanceNavigationTiming | undefined;
    return nav?.type === 'reload';
  } catch {
    return false;
  }
}

export function readInitialAiState(
  routeMode?: string,
  options?: { fresh?: boolean },
): {
  formData: AiCampaignForm;
  aiGeneratedData: AiPreviewData | null;
} {
  if (routeMode !== 'ai') {
    return { formData: emptyAiForm(), aiGeneratedData: null };
  }
  if (options?.fresh) {
    return { formData: emptyAiForm(), aiGeneratedData: null };
  }
  const draft = loadAiCampaignDraft();
  return {
    formData: draft?.formData ?? emptyAiForm(),
    aiGeneratedData: draft?.aiGeneratedData ?? null,
  };
}

function emptyStandardState() {
  return {
    currentStep: 1,
    campaignData: emptyManualForm(),
    stepTwoData: { billboardIds: [] as string[], campaignId: '' },
    selectedScreensMeta: {} as Record<string, SelectedScreenMeta>,
    submitData: { bookingSlots: [], campaignId: '' } as StepThreeData,
    previewData: {} as PreviewData,
    creativePreview: { url: '', type: '' } as CreativePreview,
    skipScreenStep: false,
  };
}

function fromBillboardLaunch(launch: BillboardCreateLaunchState) {
  const id = launch.billboardId;
  const start = launch.startDate || '';
  const end = launch.endDate || start;
  return {
    currentStep: 1,
    campaignData: {
      ...emptyManualForm(),
      startDate: start,
      endDate: end,
      targetCities: launch.city ? [launch.city] : [],
    },
    stepTwoData: { billboardIds: [id], campaignId: '' },
    selectedScreensMeta: {
      [id]: {
        id,
        screenName: launch.screenName || 'Screen',
        thumbnail: launch.thumbnail || '',
        addressLine: launch.addressLine || '',
        city: launch.city || 'Unknown',
      },
    },
    submitData: { bookingSlots: [], campaignId: '' } as StepThreeData,
    previewData: {} as PreviewData,
    creativePreview: { url: '', type: '' } as CreativePreview,
    skipScreenStep: true,
  };
}

export function readInitialStandardState(
  routeMode?: string,
  billboardLaunch?: BillboardCreateLaunchState | null,
  options?: { fresh?: boolean },
) {
  if (routeMode !== 'standard') return emptyStandardState();

  // Billboard-detail launch always starts fresh with that screen locked in.
  if (billboardLaunch?.billboardId) {
    return fromBillboardLaunch(billboardLaunch);
  }

  // Explicit new-campaign start — never restore stale city/dates/step.
  if (options?.fresh) {
    clearStandardCampaignDraft();
    return emptyStandardState();
  }

  const draft = loadStandardCampaignDraft();
  if (!draft) return emptyStandardState();

  // Step-1-only drafts (city/dates typed then left) should not come back on
  // the next visit. Keep them only on an actual browser refresh mid-step-1.
  if (draft.currentStep <= 1 && !isBrowserReload()) {
    clearStandardCampaignDraft();
    return emptyStandardState();
  }

  const campaignData = { ...emptyManualForm(), ...draft.campaignData };
  const storedPreview = draft.creativePreview?.url?.startsWith('blob:')
    ? { url: '', type: '' }
    : draft.creativePreview || { url: '', type: '' };

  return {
    currentStep: draft.currentStep,
    campaignData,
    stepTwoData: draft.stepTwoData || { billboardIds: [], campaignId: '' },
    selectedScreensMeta: draft.selectedScreensMeta || {},
    submitData: draft.submitData || { bookingSlots: [], campaignId: '' },
    previewData: draft.previewData || {},
    creativePreview: resolveCreativePreview(storedPreview, campaignData),
    skipScreenStep: Boolean(draft.skipScreenStep),
  };
}
